import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { setCurrentModule } from "../../../store/reducers/progressionReducer";
import { ComparatifCanvas } from "./ComparatifModal3D";
import styles from "./ComparatifModal.module.css";
import { logEvent } from "../../../analytics";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Modale Comparatif - Couches stacking en Y avec R3F
 */
export default function ComparatifModal({
  title = "POSITIONING VS KEY COMPETITORS EDOF*",
  subtitle = "This cube represents key EDOF lenses in the market* classified across 3 axes:",
  gridData = [],
  products = [],
}) {
  const dispatch = useDispatch();
  const [stepIdx, setStepIdx] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showInfo, setShowInfo] = useState(true); // Ouvert par défaut
  const animationTimerRef = useRef(null);

  const totalSteps = useMemo(() => {
    return 8; // 8 étapes : 4 layers × 2 phases (gris clair puis couleur)
  }, []);

  // Événement GTM : Ouverture du modal
  useEffect(() => {
    logEvent('modal_interaction', 'comparatif_modal_opened');
  }, []);

  // Détection du mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setStepIdx((prev) => clamp(prev, 0, totalSteps - 1));
  }, [totalSteps]);

  // Événement GTM : Navigation entre les steps
  useEffect(() => {
    if (stepIdx > 0) {
      logEvent('modal_interaction', 'comparatif_step_changed', `step_${stepIdx}`);
    }
  }, [stepIdx]);

  // Nettoyer le timer d'animation au démontage
  useEffect(() => {
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, []);

  // Fonction pour animer progressivement jusqu'à une étape cible
  const animateToStep = useCallback((targetStep, currentStep) => {
    // Nettoyer le timer précédent s'il existe
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }

    if (currentStep >= targetStep) {
      return; // Déjà à l'étape cible ou au-delà
    }

    // Passer à l'étape suivante
    const nextStep = currentStep + 1;
    setStepIdx(nextStep);

    // Continuer l'animation si on n'a pas atteint la cible
    if (nextStep < targetStep) {
      animationTimerRef.current = setTimeout(() => {
        animateToStep(targetStep, nextStep);
      }, 600); // Délai de 600ms entre chaque étape
    }
  }, []);

  const close = useCallback(() => {
    logEvent('modal_interaction', 'comparatif_modal_closed', `final_step_${stepIdx}`);
    dispatch(setCurrentModule("none"));
  }, [dispatch, stepIdx]);

  const goPrev = useCallback(() => {
    setStepIdx((prev) => clamp(prev - 1, 0, totalSteps - 1));
  }, [totalSteps]);

  const goNext = useCallback(() => {
    setStepIdx((prev) => clamp(prev + 1, 0, totalSteps - 1));
    // Fermer le panneau d'informations quand on clique sur suivant
    if (showInfo) {
      setShowInfo(false);
    }
  }, [totalSteps, showInfo]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
      if (e.key === "ArrowRight" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", onKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [close, goPrev, goNext]);

  // Gestion du swipe pour mobile
  useEffect(() => {
    if (!isMobile) return;

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe gauche - suivant
          goNext();
        } else {
          // Swipe droite - précédent
          goPrev();
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, goPrev, goNext]);

  const canPrev = stepIdx > 0;
  const canNext = stepIdx < totalSteps - 1;

  const buildLabels = [
    "Étape 0: Partial Range of Field Enhance (gris clair)",
    "Étape 1: Enhanced Monofocal / Monofocal + (gris foncé)",
    "Étape 2: Partial Range of Field Extended (gris clair)",
    "Étape 3: Extended Range EDOF like (bleu)",
    "Étape 4: Full Range of Field Continuous (gris clair)",
    "Étape 5: Extended Range EDOF like (beige/orange)",
    "Étape 6: Full Range of Field Smooth (gris clair)",
    "Étape 7: Trifocal / Multifocal (rose/violet)",
  ];

  const categoryLabels = [
    { name: "Enhanced Monofocal / Monofocal +", color: "#626262" },
    { name: "Extended Range EDOF like", color: "#a7c6e0" },
    { name: "Extended Range EDOF like", color: "#f8dccd" },
    { name: "Trifocal / Multifocal", color: "#e9c4d4" },
  ];

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-label={title}>
      <button className={styles.close} onClick={close} aria-label="Fermer" />

      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h2 className={styles.title}>{title}</h2>
          <button
            className={styles.infoToggle}
            onClick={() => setShowInfo(!showInfo)}
            aria-label="Toggle information"
            type="button"
          >
            <span className={styles.infoIcon}>ⓘ</span>
            {showInfo ? 'Hide details' : 'Show details'}
          </button>
        </div>

        {showInfo && (
          <div className={styles.infoPanel}>
            <p className={styles.subtitle}>{subtitle}</p>

            <div className={styles.axesInfo}>
              <div className={styles.axisItem}>
                <strong>Technology</strong> – refractive vs diffractive
              </div>
              <div className={styles.axisItem}>
                <strong>Pricing</strong> – premium vs competitive priced within premium IOL segment
              </div>
              <div className={styles.axisItem}>
                <strong>Range of Vision</strong> – according to functional classification study**
              </div>
            </div>

            <div className={styles.references}>
              <p className={styles.infoText}>
                <strong>*</strong> Based on 2025 market study & classification research
              </p>
              <p className={styles.infoText}>
                <strong>**</strong> Joaquin Fernández, PhD, Filomena Ribeiro, PhD, Carlos Rocha-de-Lossada, PhD, and Manuel Rodriguez-Vallejo, PhD
              </p>
            </div>
          </div>
        )}
      </div>

      <div className={styles.stage}>
        {/* Overlay grisé quand le panneau d'info est ouvert */}
        {showInfo && <div className={styles.overlay} onClick={() => setShowInfo(false)} />}
        
        <div className={styles.canvasContainer}>
          {/* Titres des layers - à gauche (desktop uniquement) */}
          {!isMobile && (
            <div className={styles.layerTitlesLeft}>
              <div className={`${styles.layerTitleItem} ${styles.layer0} ${stepIdx >= 0 ? styles.layerTitleVisible : ''}`}>
                Partial Range of Field<br />Enhance
              </div>
              <div className={`${styles.layerTitleItem} ${styles.layer1} ${stepIdx >= 2 ? styles.layerTitleVisible : ''}`}>
                Partial Range of Field<br />Extended
              </div>
              <div className={`${styles.layerTitleItem} ${styles.layer2} ${stepIdx >= 4 ? styles.layerTitleVisible : ''}`}>
                Full Range of Field<br />Continuous
              </div>
              <div className={`${styles.layerTitleItem} ${styles.layer3} ${stepIdx >= 6 ? styles.layerTitleVisible : ''}`}>
                Full Range of Field<br />Smooth
              </div>
            </div>
          )}

          {/* Légendes des catégories - fixe à droite (desktop uniquement) */}
          {!isMobile && (
            <div className={styles.categoriesLegend}>
              <div className={`${styles.legendItem} ${styles.legend0} ${stepIdx >= 1 ? styles.legendVisible : ''}`}>
                Enhanced Monofocal<br />Monofocal +
              </div>
              <div className={`${styles.legendItem} ${styles.legend1} ${stepIdx >= 3 ? styles.legendVisible : ''}`}>
                Extended Range<br />EDOF like
              </div>
              <div className={`${styles.legendItem} ${styles.legend2} ${stepIdx >= 5 ? styles.legendVisible : ''}`}>
                Extended Range<br />EDOF like
              </div>
              <div className={`${styles.legendItem} ${styles.legend3} ${stepIdx >= 7 ? styles.legendVisible : ''}`}>
                Trifocal<br />Multifocal
              </div>
            </div>
          )}

          {/* Boutons de sélection des produits - au-dessus du canvas, visibles dès le début */}
          {products.length > 0 && (
            <div className={styles.productsBar}>
              {products.map((prod, idx) => {
                const isSelected = selectedProduct === idx;
                return (
                  <button
                    key={`prod-${prod.id}`}
                    className={`${styles.productBtn} ${isSelected ? styles.productBtnSelected : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newSelection = isSelected ? null : idx;
                      setSelectedProduct(newSelection);
                      
                      // Événement GTM : Sélection/désélection de produit
                      if (newSelection !== null) {
                        logEvent('modal_interaction', 'comparatif_product_selected', prod.name);
                        // Si on sélectionne un produit, animer progressivement jusqu'à la dernière étape
                        animateToStep(7, stepIdx);
                      } else {
                        logEvent('modal_interaction', 'comparatif_product_deselected', prod.name);
                      }
                    }}
                    style={{
                      '--product-color': prod.color,
                    }}
                    type="button"
                  >
                    <span className={styles.productDot} style={{ background: prod.color }} />
                    {prod.name}
                  </button>
                );
              })}
            </div>
          )}

          <ComparatifCanvas
            gridData={gridData}
            stepIdx={stepIdx}
            products={products}
            selectedProduct={selectedProduct}
            selectedColumn={selectedColumn}
            onColumnSelect={setSelectedColumn}
            isMobile={isMobile}
          />
        </div>

        {/* Légende mobile - affichée sous le canvas sur mobile */}
        {isMobile && (
          <div className={styles.mobileLegend}>
            {categoryLabels.map((category, idx) => {
              // Afficher les catégories selon les étapes impaires (1, 3, 5, 7)
              const categoryStep = idx * 2 + 1;
              if (stepIdx < categoryStep) return null;
              return (
                <div
                  key={`mobile-legend-${idx}`}
                  className={`${styles.mobileLegendItem} ${stepIdx >= categoryStep ? styles.mobileLegendVisible : ''}`}
                  style={{ '--legend-color': category.color }}
                >
                  <span className={styles.mobileLegendDot} />
                  <span className={styles.mobileLegendText}>{category.name}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.hint}>
          <span>Click or Space to go to the next layer</span>
          <span className={styles.hintKeys}>Esc · ←/→ · Space</span>
        </div>
      </div>

      <div className={styles.footer}>
        <button
          className={`${styles.btn} ${styles.secondary}`}
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          disabled={!canPrev}
          type="button"
        >
          ← Previous
        </button>
        <button
          className={`${styles.btn} ${styles.primary}`}
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          disabled={!canNext}
          type="button"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
